/**
 * Onboarding Flow Hook for Community Onboarding
 * Handles step navigation, flow control, and overall onboarding state
 */

import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { TierType } from '../../constants/onboarding/tierConfiguration';
import type { UseOnboardingFlowReturn, OnboardingNavigation } from '../../types/onboarding';

export const useOnboardingFlow = (): UseOnboardingFlowReturn => {
  const [searchParams] = useSearchParams();
  const selectedTier = searchParams.get('tier') as TierType | null;

  const [currentStep, setCurrentStep] = useState(1);

  // Determine flow type and total steps
  const isProgressiveFlow = !selectedTier;
  const totalSteps = isProgressiveFlow ? 9 : 5;
  const progress = (currentStep / totalSteps) * 100;

  const nextStep = useCallback(() => {
    if (currentStep < totalSteps) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, totalSteps]);

  const prevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback(
    (step: number) => {
      if (step >= 1 && step <= totalSteps) {
        setCurrentStep(step);
      }
    },
    [totalSteps],
  );

  const navigation: OnboardingNavigation = useMemo(
    () => ({
      nextStep,
      prevStep,
      goToStep,
      canProceed: currentStep < totalSteps,
      canGoBack: currentStep > 1,
    }),
    [nextStep, prevStep, goToStep, currentStep, totalSteps],
  );

  return {
    currentStep,
    totalSteps,
    isProgressiveFlow,
    progress,
    navigation,
  };
};
